import { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Banner that surfaces banks needing re-authentication (Plaid ITEM_LOGIN_REQUIRED
 * or PENDING_EXPIRATION) and lets the user open Plaid Link in update-mode to
 * fix them in one click. Renders nothing if no banks need re-auth.
 */
export default function RelinkBankBanner({ bankAccounts, onReconnected }) {
  const needs = (bankAccounts || []).filter((b) => b?.needs_reauth || b?.status === 'error');
  if (needs.length === 0) return null;

  return (
    <Card
      className="border-amber-500/40 bg-amber-500/5"
      data-testid="relink-banner"
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">
                {needs.length === 1
                  ? '1 bank needs reconnection'
                  : `${needs.length} banks need reconnection`}
              </span>
              <Badge
                variant="outline"
                className="border-amber-500/30 text-amber-500 text-[10px]"
              >
                Action required
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Your bank requires you to re-authenticate. Transactions will resume syncing
              automatically once reconnected.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {needs.map((bank) => (
                <RelinkButton
                  key={bank.bank_id}
                  bank={bank}
                  onReconnected={onReconnected}
                />
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RelinkButton({ bank, onReconnected }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchToken = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/plaid/relink_token?bank_id=${encodeURIComponent(bank.bank_id)}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setLinkToken(data.link_token);
    } catch (e) {
      toast.error(`Could not start re-link: ${e.message}`);
      setLoading(false);
    }
  }, [bank.bank_id]);

  const onSuccess = useCallback(async () => {
    try {
      const r = await fetch(
        `${API_URL}/api/plaid/clear_reauth?bank_id=${encodeURIComponent(bank.bank_id)}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      toast.success(`${bank.institution_name || 'Bank'} reconnected`);
      onReconnected?.();
    } catch (e) {
      toast.error(`Reconnected, but status update failed: ${e.message}`);
    } finally {
      setLinkToken(null);
      setLoading(false);
    }
  }, [bank.bank_id, bank.institution_name, onReconnected]);

  const onExit = useCallback((err) => {
    setLinkToken(null);
    setLoading(false);
    if (err) {
      toast.error('Re-link cancelled or failed');
    }
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
  });

  // Auto-open the moment Plaid Link is ready after fetching the token
  useEffect(() => {
    if (linkToken && ready) open();
  }, [linkToken, ready, open]);

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={fetchToken}
      disabled={loading}
      className="border-amber-500/40 text-amber-600 hover:bg-amber-500/10 dark:text-amber-400"
      data-testid={`relink-bank-${bank.bank_id}`}
    >
      {loading
        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
      Reconnect {bank.institution_name || 'bank'}
    </Button>
  );
}
